/**
 * Balance Sheet Category options and inference logic.
 *
 * Each account can be assigned a `balanceSheetCategory` that maps directly
 * to a column in the `balance_sheet_monthly` Supabase table.  The camelCase
 * values here are converted to snake_case by the store's toSnakeCase helper.
 */

// Grouped options for <select> / <optgroup> rendering
export const BS_CATEGORY_OPTIONS = [
  {
    group: 'Cash & Cash Equivalents',
    options: [
      { value: 'cashOnHand', label: 'Cash on Hand' },
      { value: 'checking', label: 'Checking Accounts' },
      { value: 'savings', label: 'Savings Accounts' },
      { value: 'tBills', label: 'T-Bills' },
    ],
  },
  {
    group: 'Investments (Brokerage)',
    options: [
      { value: 'robinhood', label: 'Robinhood' },
      { value: 'fundrise', label: 'Fundrise' },
    ],
  },
  {
    group: 'Investments (Tax Advantage)',
    options: [
      { value: 'roth401k', label: 'Roth 401K' },
      { value: 'traditionalIra', label: 'Traditional IRA' },
      { value: 'rothIra', label: 'IRA' },
      { value: 'hsa', label: 'HSA' },
    ],
  },
  {
    group: 'Property',
    options: [
      { value: 'vehicle', label: 'Vehicle' },
    ],
  },
  {
    group: 'Other Assets',
    options: [
      { value: 'lifeInsurance', label: 'Life Insurance' },
      { value: 'loyaltyPoints', label: 'Loyalty Points Value' },
      { value: 'accountsReceivable', label: 'Accounts Receivable' },
      { value: 'securityDeposit', label: 'Security Deposit' },
      { value: 'ownershiftEquity', label: 'Private Equity' },
      { value: 'collectables', label: 'Collectables' },
    ],
  },
  {
    group: 'Liabilities',
    options: [
      { value: 'creditCards', label: 'Credit Cards' },
      { value: 'loansFromFriends', label: 'Accounts Payable' },
      { value: 'vehicleLoan', label: 'Vehicle Payable' },
    ],
  },
];

// Flat lookup: value → label  (e.g. 'checking' → 'Checking Accounts')
export const BS_CATEGORY_LABEL = {};
BS_CATEGORY_OPTIONS.forEach((g) =>
  g.options.forEach((o) => { BS_CATEGORY_LABEL[o.value] = o.label; })
);

/**
 * Auto-suggest a balance sheet category based on account type and name.
 * Used as a fallback when the user hasn't explicitly chosen one.
 *
 * @param {{ type?: string, name?: string }} account
 * @returns {string|null} camelCase category key, or null if unknown
 */
export function inferBalanceSheetCategory(account) {
  const name = (account.name || '').toLowerCase();
  const type = (account.type || '').toLowerCase();

  // ── Simple type → category ──
  if (type === 'cash') return 'cashOnHand';
  if (type === 'checking') return 'checking';
  if (type === 'savings') return 'savings';

  // ── Investments — match by name patterns ──
  if (type === 'investment') {
    if (/t.?bill/i.test(name) || /treasury\s*direct/i.test(name)) return 'tBills';
    if (/401k|401\(k\)/i.test(name)) return 'roth401k';
    if (/traditional\s*ira/i.test(name)) return 'traditionalIra';
    if (/roth\s*ira/i.test(name)) return 'rothIra';
    if (/hsa/i.test(name)) return 'hsa';
    if (/robinhood/i.test(name)) return 'robinhood';
    if (/fundrise/i.test(name)) return 'fundrise';
    return null;
  }

  // ── Property ──
  if (type === 'property') {
    if (/vehicle/i.test(name)) return 'vehicle';
    return 'vehicle'; // default property → vehicle
  }

  // ── Other assets ──
  if (type === 'other') {
    if (/life\s*insurance|whole\s*life|term\s*life|20pay/i.test(name)) return 'lifeInsurance';
    if (/loyalty/i.test(name)) return 'loyaltyPoints';
    if (/receivable/i.test(name)) return 'accountsReceivable';
    if (/security\s*deposit/i.test(name)) return 'securityDeposit';
    if (/equity|stock\s*option|vesting/i.test(name)) return 'ownershiftEquity';
    if (/collectab/i.test(name)) return 'collectables';
    return null;
  }

  // ── Loans / liabilities ──
  if (type === 'loan') {
    if (/vehicle/i.test(name)) return 'vehicleLoan';
    if (/payable|friend|family/i.test(name)) return 'loansFromFriends';
    return 'loansFromFriends'; // default loan
  }

  return null;
}
